import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Search, Filter, Building2, AlertTriangle, User, Calendar,
  CreditCard, FileText, CheckCircle, Clock, ArrowRight, Euro,
  TrendingUp, AlertCircle, FileCheck, RefreshCw, Loader2, Edit, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { estafasBancariasAPI, contactosAPI } from '../api';
import { toast } from 'sonner';

// Configuración de estados con colores
const ESTADOS_CONFIG = {
  inicio: { label: 'Inicio', color: 'bg-gray-100 text-gray-800', icon: Clock },
  documentacion_denuncia: { label: 'Denuncia', color: 'bg-blue-100 text-blue-800', icon: FileText },
  reclamacion_banco: { label: 'Reclamación Banco', color: 'bg-yellow-100 text-yellow-800', icon: Building2 },
  contrato_bancario: { label: 'Contrato', color: 'bg-orange-100 text-orange-800', icon: FileCheck },
  reclamacion_extrajudicial: { label: 'Reclamación Ext.', color: 'bg-purple-100 text-purple-800', icon: FileText },
  hoja_encargo: { label: 'Hoja Encargo', color: 'bg-indigo-100 text-indigo-800', icon: FileText },
  informe_resumen: { label: 'Informe', color: 'bg-pink-100 text-pink-800', icon: FileText },
  envio_banco: { label: 'Enviado', color: 'bg-cyan-100 text-cyan-800', icon: ArrowRight },
  respuesta_banco: { label: 'Respuesta', color: 'bg-teal-100 text-teal-800', icon: Building2 },
  expediente_creado: { label: 'Expediente', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const TIPOS_ESTAFA = [
  { id: 'phishing', nombre: 'Phishing (correo fraudulento)' },
  { id: 'vishing', nombre: 'Vishing (llamada fraudulenta)' },
  { id: 'smishing', nombre: 'Smishing (SMS fraudulento)' },
  { id: 'sim_swapping', nombre: 'SIM Swapping (duplicado de SIM)' },
  { id: 'pharming', nombre: 'Pharming (web falsa)' },
  { id: 'man_in_the_middle', nombre: 'Man in the middle (interceptación)' },
  { id: 'robo_tarjeta', nombre: 'Robo/clonación de tarjeta' },
  { id: 'acceso_no_autorizado', nombre: 'Acceso no autorizado a banca online' },
  { id: 'otro', nombre: 'Otro (especificar)' },
];

const EstafasBancarias = () => {
  const navigate = useNavigate();
  const [casos, setCasos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');
  
  // Modal nuevo caso
  const [showNuevoCaso, setShowNuevoCaso] = useState(false);
  const [contactos, setContactos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [searchContacto, setSearchContacto] = useState('');
  const [searchBanco, setSearchBanco] = useState('');
  const [creandoCaso, setCreandoCaso] = useState(false);
  const [nuevoCaso, setNuevoCaso] = useState({
    cliente_id: '',
    banco_id: '',
    banco_nombre: '',
    tipo_estafa: '',
    tipo_estafa_otro: '',
    fecha_fraude: '',
    lugar_fraude: '',
    cantidad_estafada: '',
    numero_cuenta: '',
    numero_tarjeta: '',
    descripcion_hechos: ''
  });
  
  // Estados para crear nuevo cliente
  const [isCreatingCliente, setIsCreatingCliente] = useState(false);
  const [savingCliente, setSavingCliente] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState({
    nombre: '', primer_apellido: '', segundo_apellido: '', nif_cif: '', 
    direccion: '', codigo_postal: '', ciudad: '', provincia: '', 
    telefono: '', email: '', notas: ''
  });
  
  // Estados para crear nuevo banco
  const [isCreatingBanco, setIsCreatingBanco] = useState(false);
  const [savingBanco, setSavingBanco] = useState(false);
  const [nuevoBanco, setNuevoBanco] = useState({
    nombre: '', razon_social: '', nif_cif: '', 
    direccion: '', codigo_postal: '', ciudad: '', provincia: '', 
    telefono: '', email: '', notas: ''
  });
  
  // Estado para eliminar caso
  const [casoAEliminar, setCasoAEliminar] = useState(null);
  const [eliminandoCaso, setEliminandoCaso] = useState(false);

  useEffect(() => {
    loadData();
  }, [search, filtroEstado, filtroTipo]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [casosRes, statsRes] = await Promise.all([
        estafasBancariasAPI.getAll({ 
          search: search || undefined,
          estado: filtroEstado !== 'all' ? filtroEstado : undefined,
          tipo_estafa: filtroTipo !== 'all' ? filtroTipo : undefined
        }),
        estafasBancariasAPI.getStats()
      ]);
      setCasos(casosRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast.error('Error al cargar los casos');
    } finally {
      setLoading(false);
    }
  };

  const searchContactos = async (query) => {
    if (query.length < 2) return;
    try {
      const res = await contactosAPI.getAll({ search: query, limit: 10 });
      const data = res.data?.data || res.data?.contactos || res.data || [];
      setContactos(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error buscando contactos:', error);
      setContactos([]);
    }
  };

  const searchBancos = async (query) => {
    if (query.length < 2) return;
    try {
      // Buscar en todos los contactos que contengan el término
      const res = await contactosAPI.getAll({ search: query, limit: 20 });
      const data = res.data?.data || res.data?.contactos || res.data || [];
      // Filtrar solo empresas/entidades si es posible, sino mostrar todos
      const resultados = Array.isArray(data) ? data : [];
      setBancos(resultados);
    } catch (error) {
      console.error('Error buscando bancos:', error);
      setBancos([]);
    }
  };

  // Crear nuevo cliente (víctima)
  const handleCrearCliente = async () => {
    if (!nuevoCliente.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSavingCliente(true);
    try {
      const nombreCompleto = [nuevoCliente.nombre, nuevoCliente.primer_apellido, nuevoCliente.segundo_apellido]
        .filter(Boolean).join(' ');
      
      const res = await contactosAPI.create({
        nombre: nuevoCliente.nombre,
        primer_apellido: nuevoCliente.primer_apellido || null,
        segundo_apellido: nuevoCliente.segundo_apellido || null,
        tipo_entidad: 'persona',
        tipo: 'cliente',
        nif_cif: nuevoCliente.nif_cif || '',
        direccion: nuevoCliente.direccion || '',
        codigo_postal: nuevoCliente.codigo_postal || '',
        ciudad: nuevoCliente.ciudad || '',
        provincia: nuevoCliente.provincia || '',
        telefono: nuevoCliente.telefono || '',
        email: nuevoCliente.email || '',
        notas: nuevoCliente.notas || ''
      });
      
      const clienteCreado = res.data;
      setNuevoCaso({...nuevoCaso, cliente_id: clienteCreado.id});
      setSearchContacto(clienteCreado.nombre_completo || nombreCompleto);
      setIsCreatingCliente(false);
      setNuevoCliente({ nombre: '', primer_apellido: '', segundo_apellido: '', nif_cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', telefono: '', email: '', notas: '' });
      setContactos([]);
      toast.success(`Cliente "${nombreCompleto}" creado correctamente`);
    } catch (error) {
      console.error('Error creando cliente:', error);
      toast.error('No se pudo crear el cliente');
    } finally {
      setSavingCliente(false);
    }
  };

  // Crear nuevo banco/entidad
  const handleCrearBanco = async () => {
    if (!nuevoBanco.nombre) {
      toast.error('El nombre es obligatorio');
      return;
    }
    setSavingBanco(true);
    try {
      const res = await contactosAPI.create({
        nombre: nuevoBanco.nombre,
        razon_social: nuevoBanco.razon_social || nuevoBanco.nombre,
        tipo_entidad: 'empresa',
        tipo: 'parte_contraria',
        nif_cif: nuevoBanco.nif_cif || '',
        direccion: nuevoBanco.direccion || '',
        codigo_postal: nuevoBanco.codigo_postal || '',
        ciudad: nuevoBanco.ciudad || '',
        provincia: nuevoBanco.provincia || '',
        telefono: nuevoBanco.telefono || '',
        email: nuevoBanco.email || '',
        notas: nuevoBanco.notas || ''
      });
      
      const bancoCreado = res.data;
      setNuevoCaso({
        ...nuevoCaso, 
        banco_id: bancoCreado.id,
        banco_nombre: bancoCreado.razon_social || bancoCreado.nombre
      });
      setSearchBanco(bancoCreado.razon_social || bancoCreado.nombre);
      setIsCreatingBanco(false);
      setNuevoBanco({ nombre: '', razon_social: '', nif_cif: '', direccion: '', codigo_postal: '', ciudad: '', provincia: '', telefono: '', email: '', notas: '' });
      setBancos([]);
      toast.success(`Entidad "${bancoCreado.nombre}" creada correctamente`);
    } catch (error) {
      console.error('Error creando banco:', error);
      toast.error('No se pudo crear la entidad bancaria');
    } finally {
      setSavingBanco(false);
    }
  };

  const handleCrearCaso = async () => {
    // Validaciones
    if (!nuevoCaso.cliente_id) {
      toast.error("Seleccione un cliente");
      return;
    }
    if (!nuevoCaso.banco_id && !nuevoCaso.banco_nombre) {
      toast.error("Seleccione o indique una entidad bancaria");
      return;
    }
    if (!nuevoCaso.tipo_estafa) {
      toast.error("Seleccione el tipo de estafa");
      return;
    }
    if (!nuevoCaso.fecha_fraude) {
      toast.error("Indique la fecha del fraude");
      return;
    }
    if (!nuevoCaso.lugar_fraude) {
      toast.error("Indique el lugar del fraude");
      return;
    }
    if (!nuevoCaso.cantidad_estafada || parseFloat(nuevoCaso.cantidad_estafada) <= 0) {
      toast.error("Indique la cantidad estafada");
      return;
    }

    setCreandoCaso(true);
    
    try {
      const dataToSend = {
        cliente_id: parseInt(nuevoCaso.cliente_id),
        banco_id: nuevoCaso.banco_id ? parseInt(nuevoCaso.banco_id) : null,
        banco_nombre: nuevoCaso.banco_nombre,
        tipo_estafa: nuevoCaso.tipo_estafa,
        tipo_estafa_otro: nuevoCaso.tipo_estafa_otro || null,
        fecha_fraude: nuevoCaso.fecha_fraude,
        lugar_fraude: nuevoCaso.lugar_fraude,
        cantidad_estafada: parseFloat(nuevoCaso.cantidad_estafada),
        numero_cuenta: nuevoCaso.numero_cuenta || null,
        numero_tarjeta: nuevoCaso.numero_tarjeta || null,
        descripcion_hechos: nuevoCaso.descripcion_hechos || null
      };
      
      console.log('Enviando datos:', dataToSend);
      const res = await estafasBancariasAPI.create(dataToSend);
      console.log('Respuesta:', res.data);
      
      toast.success(`Caso ${res.data.numero_referencia} creado correctamente`);
      setShowNuevoCaso(false);
      // Recargar la lista
      loadData();
      // Limpiar formulario
      setNuevoCaso({
        cliente_id: '',
        banco_id: '',
        banco_nombre: '',
        tipo_estafa: '',
        tipo_estafa_otro: '',
        fecha_fraude: '',
        lugar_fraude: '',
        cantidad_estafada: '',
        numero_cuenta: '',
        numero_tarjeta: '',
        descripcion_hechos: ''
      });
      setSearchContacto('');
      setSearchBanco('');
    } catch (error) {
      console.error('Error creando caso:', error);
      const errorMsg = error.response?.data?.detail || 'Error al crear el caso';
      toast.error(errorMsg);
    } finally {
      setCreandoCaso(false);
    }
  };
  
  const handleEliminarCaso = async () => {
    if (!casoAEliminar) return;
    
    setEliminandoCaso(true);
    try {
      await estafasBancariasAPI.delete(casoAEliminar.id);
      toast.success(`Caso ${casoAEliminar.numero_referencia} eliminado`);
      setCasoAEliminar(null);
      loadData();
    } catch (error) {
      console.error('Error eliminando caso:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar el caso');
    } finally {
      setEliminandoCaso(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getEstadoConfig = (estado) => ESTADOS_CONFIG[estado] || ESTADOS_CONFIG.inicio;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-7 h-7 text-red-500" />
            Estafas Bancarias
          </h1>
          <p className="text-gray-500 mt-1">Gestión de reclamaciones por fraude bancario</p>
        </div>
        <Button onClick={() => setShowNuevoCaso(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Nuevo Caso
        </Button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Casos</p>
                  <p className="text-2xl font-bold">{stats.total}</p>
                </div>
                <FileText className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Cantidad Total</p>
                  <p className="text-2xl font-bold text-red-600">
                    {formatCurrency(stats.total_cantidad_estafada)}
                  </p>
                </div>
                <Euro className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pendientes</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.pendientes}</p>
                </div>
                <Clock className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Expedientes</p>
                  <p className="text-2xl font-bold text-green-600">{stats.convertidos_expediente}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Buscar por referencia, cliente o banco..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(ESTADOS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Tipo de estafa" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {TIPOS_ESTAFA.map((tipo) => (
                  <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadData} className="gap-2">
              <RefreshCw className="w-4 h-4" />
              Actualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de casos */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : casos.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <AlertCircle className="w-12 h-12 mb-4" />
              <p>No hay casos registrados</p>
              <Button variant="link" onClick={() => setShowNuevoCaso(true)}>
                Crear nuevo caso
              </Button>
            </div>
          ) : (
            <div className="divide-y">
              {casos.map((caso) => {
                const estadoConfig = getEstadoConfig(caso.estado);
                const EstadoIcon = estadoConfig.icon;
                return (
                  <div
                    key={caso.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => navigate(`/estafas-bancarias/${caso.id}`)}
                      >
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-gray-500">
                            {caso.numero_referencia}
                          </span>
                          <Badge className={estadoConfig.color}>
                            <EstadoIcon className="w-3 h-3 mr-1" />
                            {estadoConfig.label}
                          </Badge>
                          {caso.expediente_id && (
                            <Badge variant="outline" className="text-green-600">
                              Expediente #{caso.expediente_id}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4 text-gray-400" />
                            {caso.cliente_nombre}
                          </span>
                          <span className="flex items-center gap-1">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            {caso.banco_nombre}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {new Date(caso.fecha_fraude).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            {formatCurrency(caso.cantidad_estafada)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {TIPOS_ESTAFA.find(t => t.id === caso.tipo_estafa)?.nombre || caso.tipo_estafa}
                          </p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/estafas-bancarias/${caso.id}`);
                            }}
                            title="Ver/Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              setCasoAEliminar(caso);
                            }}
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Nuevo Caso */}
      <Dialog open={showNuevoCaso} onOpenChange={setShowNuevoCaso}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Nuevo Caso de Estafa Bancaria
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Cliente */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Cliente (víctima) *</Label>
              
              {!isCreatingCliente ? (
                <>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Buscar cliente por nombre, DNI o email..."
                        value={searchContacto}
                        onChange={(e) => {
                          setSearchContacto(e.target.value);
                          setNuevoCaso({...nuevoCaso, cliente_id: ''});
                          searchContactos(e.target.value);
                        }}
                        disabled={!!nuevoCaso.cliente_id}
                      />
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setIsCreatingCliente(true);
                        setNuevoCliente({...nuevoCliente, nombre: searchContacto});
                      }}
                      title="Crear nuevo cliente"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {nuevoCaso.cliente_id && (
                    <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700">✓ Cliente seleccionado: <strong>{searchContacto}</strong></p>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setNuevoCaso({...nuevoCaso, cliente_id: ''});
                          setSearchContacto('');
                        }}
                      >
                        Cambiar
                      </Button>
                    </div>
                  )}
                  
                  {contactos.length > 0 && searchContacto && !nuevoCaso.cliente_id && (
                    <div className="border rounded-md max-h-40 overflow-y-auto bg-white shadow-sm">
                      {contactos.map((c) => (
                        <div
                          key={c.id}
                          className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setNuevoCaso({...nuevoCaso, cliente_id: c.id});
                            setSearchContacto(c.nombre_completo || c.nombre);
                            setContactos([]);
                          }}
                        >
                          <p className="font-medium">{c.nombre_completo || c.nombre}</p>
                          <p className="text-xs text-gray-500">
                            {c.nif_cif || ''} 
                            {c.telefono ? ` - Tel: ${c.telefono}` : ''}
                            {c.email ? ` - ${c.email}` : ''}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchContacto.length >= 2 && contactos.length === 0 && !nuevoCaso.cliente_id && (
                    <div className="p-3 bg-orange-50 border border-orange-200 rounded-md">
                      <p className="text-sm text-orange-700">No se encontró "{searchContacto}"</p>
                      <Button 
                        type="button" 
                        variant="outline" 
                        size="sm"
                        className="mt-2"
                        onClick={() => {
                          setIsCreatingCliente(true);
                          setNuevoCliente({...nuevoCliente, nombre: searchContacto});
                        }}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Crear nuevo cliente
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                /* Formulario crear nuevo cliente */
                <div className="p-4 border-2 border-blue-200 bg-blue-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-blue-800">Nuevo Cliente (víctima)</p>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreatingCliente(false)}>
                      ✕
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">Nombre *</Label>
                      <Input
                        value={nuevoCliente.nombre}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, nombre: e.target.value})}
                        placeholder="Nombre"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Primer Apellido</Label>
                      <Input
                        value={nuevoCliente.primer_apellido}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, primer_apellido: e.target.value})}
                        placeholder="Apellido 1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Segundo Apellido</Label>
                      <Input
                        value={nuevoCliente.segundo_apellido}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, segundo_apellido: e.target.value})}
                        placeholder="Apellido 2"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">DNI/NIF</Label>
                      <Input
                        value={nuevoCliente.nif_cif}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, nif_cif: e.target.value})}
                        placeholder="12345678A"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Teléfono</Label>
                      <Input
                        value={nuevoCliente.telefono}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, telefono: e.target.value})}
                        placeholder="600 000 000"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input
                      type="email"
                      value={nuevoCliente.email}
                      onChange={(e) => setNuevoCliente({...nuevoCliente, email: e.target.value})}
                      placeholder="cliente@email.com"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-xs">Dirección</Label>
                    <Input
                      value={nuevoCliente.direccion}
                      onChange={(e) => setNuevoCliente({...nuevoCliente, direccion: e.target.value})}
                      placeholder="Calle, número..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">C.P.</Label>
                      <Input
                        value={nuevoCliente.codigo_postal}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, codigo_postal: e.target.value})}
                        placeholder="08001"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ciudad</Label>
                      <Input
                        value={nuevoCliente.ciudad}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, ciudad: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Provincia</Label>
                      <Input
                        value={nuevoCliente.provincia}
                        onChange={(e) => setNuevoCliente({...nuevoCliente, provincia: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="button"
                    className="w-full" 
                    onClick={handleCrearCliente}
                    disabled={savingCliente || !nuevoCliente.nombre}
                  >
                    {savingCliente && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Crear Cliente y Seleccionar
                  </Button>
                </div>
              )}
            </div>

            {/* Banco */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Entidad Bancaria *</Label>
              
              {!isCreatingBanco ? (
                <>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        placeholder="Buscar banco por nombre..."
                        value={searchBanco}
                        onChange={(e) => {
                          setSearchBanco(e.target.value);
                          setNuevoCaso({...nuevoCaso, banco_id: '', banco_nombre: e.target.value});
                          searchBancos(e.target.value);
                        }}
                        disabled={!!nuevoCaso.banco_id}
                      />
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setIsCreatingBanco(true);
                        setNuevoBanco({...nuevoBanco, nombre: searchBanco, razon_social: searchBanco});
                      }}
                      title="Crear nueva entidad bancaria"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {nuevoCaso.banco_id && (
                    <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-md">
                      <p className="text-sm text-green-700">✓ Banco seleccionado: <strong>{searchBanco}</strong></p>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          setNuevoCaso({...nuevoCaso, banco_id: '', banco_nombre: ''});
                          setSearchBanco('');
                        }}
                      >
                        Cambiar
                      </Button>
                    </div>
                  )}
                  
                  {bancos.length > 0 && searchBanco && !nuevoCaso.banco_id && (
                    <div className="border rounded-md max-h-40 overflow-y-auto bg-white shadow-sm">
                      {bancos.map((b) => (
                        <div
                          key={b.id}
                          className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                          onClick={() => {
                            setNuevoCaso({
                              ...nuevoCaso,
                              banco_id: b.id,
                              banco_nombre: b.nombre_completo || b.razon_social || b.nombre
                            });
                            setSearchBanco(b.nombre_completo || b.razon_social || b.nombre);
                            setBancos([]);
                          }}
                        >
                          <p className="font-medium">{b.nombre_completo || b.razon_social || b.nombre}</p>
                          <p className="text-xs text-gray-500">{b.nif_cif || ''} {b.direccion || b.email || ''}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {searchBanco.length >= 2 && bancos.length === 0 && !nuevoCaso.banco_id && (
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <p className="text-sm text-gray-600">No se encontró "{searchBanco}"</p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          type="button" 
                          variant="outline" 
                          size="sm"
                          onClick={() => {
                            setIsCreatingBanco(true);
                            setNuevoBanco({...nuevoBanco, nombre: searchBanco, razon_social: searchBanco});
                          }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Crear entidad
                        </Button>
                        <span className="text-xs text-gray-500 self-center">o se usará el nombre introducido</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* Formulario crear nuevo banco */
                <div className="p-4 border-2 border-purple-200 bg-purple-50 rounded-lg space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-purple-800">Nueva Entidad Bancaria</p>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreatingBanco(false)}>
                      ✕
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">Nombre Comercial *</Label>
                      <Input
                        value={nuevoBanco.nombre}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, nombre: e.target.value})}
                        placeholder="Ej: BBVA, Santander..."
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Razón Social</Label>
                      <Input
                        value={nuevoBanco.razon_social}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, razon_social: e.target.value})}
                        placeholder="Banco Bilbao Vizcaya S.A."
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs">CIF</Label>
                      <Input
                        value={nuevoBanco.nif_cif}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, nif_cif: e.target.value})}
                        placeholder="A12345678"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Teléfono</Label>
                      <Input
                        value={nuevoBanco.telefono}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, telefono: e.target.value})}
                        placeholder="900 000 000"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-xs">Dirección</Label>
                    <Input
                      value={nuevoBanco.direccion}
                      onChange={(e) => setNuevoBanco({...nuevoBanco, direccion: e.target.value})}
                      placeholder="Dirección de la sucursal..."
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs">C.P.</Label>
                      <Input
                        value={nuevoBanco.codigo_postal}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, codigo_postal: e.target.value})}
                        placeholder="08001"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Ciudad</Label>
                      <Input
                        value={nuevoBanco.ciudad}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, ciudad: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Provincia</Label>
                      <Input
                        value={nuevoBanco.provincia}
                        onChange={(e) => setNuevoBanco({...nuevoBanco, provincia: e.target.value})}
                        placeholder="Barcelona"
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="button"
                    className="w-full" 
                    onClick={handleCrearBanco}
                    disabled={savingBanco || !nuevoBanco.nombre}
                  >
                    {savingBanco && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Crear Entidad y Seleccionar
                  </Button>
                </div>
              )}
            </div>

            {/* Tipo de estafa */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Tipo de Estafa *</Label>
              <Select
                value={nuevoCaso.tipo_estafa}
                onValueChange={(val) => setNuevoCaso({...nuevoCaso, tipo_estafa: val})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tipo..." />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_ESTAFA.map((tipo) => (
                    <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {nuevoCaso.tipo_estafa === 'otro' && (
                <Input
                  placeholder="Especificar tipo de estafa..."
                  value={nuevoCaso.tipo_estafa_otro}
                  onChange={(e) => setNuevoCaso({...nuevoCaso, tipo_estafa_otro: e.target.value})}
                />
              )}
            </div>

            {/* Datos del fraude */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Fecha del Fraude *</Label>
                <Input
                  type="date"
                  value={nuevoCaso.fecha_fraude}
                  onChange={(e) => setNuevoCaso({...nuevoCaso, fecha_fraude: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Cantidad Estafada (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="0.00"
                  value={nuevoCaso.cantidad_estafada}
                  onChange={(e) => setNuevoCaso({...nuevoCaso, cantidad_estafada: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Lugar del Fraude *</Label>
              <Input
                placeholder="Ciudad, país..."
                value={nuevoCaso.lugar_fraude}
                onChange={(e) => setNuevoCaso({...nuevoCaso, lugar_fraude: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium">Número de Cuenta</Label>
                <Input
                  placeholder="ESXX XXXX XXXX..."
                  value={nuevoCaso.numero_cuenta}
                  onChange={(e) => setNuevoCaso({...nuevoCaso, numero_cuenta: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-medium">Número de Tarjeta</Label>
                <Input
                  placeholder="XXXX XXXX XXXX XXXX"
                  value={nuevoCaso.numero_tarjeta}
                  onChange={(e) => setNuevoCaso({...nuevoCaso, numero_tarjeta: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">Descripción de los Hechos</Label>
              <Textarea
                placeholder="Describa cómo ocurrió la estafa..."
                rows={4}
                value={nuevoCaso.descripcion_hechos}
                onChange={(e) => setNuevoCaso({...nuevoCaso, descripcion_hechos: e.target.value})}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowNuevoCaso(false)} disabled={creandoCaso}>
                Cancelar
              </Button>
              <Button onClick={handleCrearCaso} disabled={creandoCaso}>
                {creandoCaso ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creando...
                  </>
                ) : (
                  'Crear Caso'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* AlertDialog para confirmar eliminación */}
      <AlertDialog open={!!casoAEliminar} onOpenChange={(open) => !open && setCasoAEliminar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="w-5 h-5" />
              Eliminar Caso de Estafa
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de eliminar el caso <strong>{casoAEliminar?.numero_referencia}</strong>?
              <br /><br />
              <span className="text-red-600">
                Esta acción eliminará permanentemente todos los documentos y el historial asociado.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={eliminandoCaso}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleEliminarCaso}
              disabled={eliminandoCaso}
              className="bg-red-600 hover:bg-red-700"
            >
              {eliminandoCaso ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EstafasBancarias;
